import * as React from 'react';
import { useQuery } from 'react-query';
import type { Company } from 'contracts/Company';
import type { CompanySiteType } from 'constants/CompanySiteType';
import { api, applyQueryString } from 'services/api';
import { Formatter } from 'utils';

import { multiSelectorOptionsMapper, optionsMapper } from '../utils';

type LabelType = 'trade-name-and-document' | 'trade-name' | 'sap-code-trade-name-and-document'
type HookOptions = {
  labelType?: LabelType,
  siteType?: CompanySiteType,
  onlyWithSapCode?: boolean,
}

const createLabel = (company: Company, labelType?: LabelType) => {
  const tradeName = company.tradeName;
  const sapCode = company.sapCode;

  const document = (() => {
    const doc = Formatter.document(company.document, company.documentType);
    const docLength = doc.replace(/\D/g, '')?.length ?? 0;
    return docLength >= 8 ? doc : null;
  })();

  switch (labelType) {
    case 'trade-name':
      return tradeName;
    case 'sap-code-trade-name-and-document':
      return [sapCode, tradeName, document].filter(Boolean).join(' - ');
    case 'trade-name-and-document':
    default:
      return [tradeName, document].filter(Boolean).join(' - ');
  }
};

export const useCompanyOptions = (options?: HookOptions) => {
  const fetchCompanies = React.useCallback(async () => {
    const url = applyQueryString('/companies', {
      asList: true,
      ...(options?.siteType && { siteType: options.siteType }),
    });

    const { data } = await api.get<Company[]>(url);
    return data;
  }, []);

  const makeOptions = React.useCallback((data?: Company[]) => {
    if (!data?.length) return [];

    const companies = (() => {
      if (options?.onlyWithSapCode) {
        return data.filter(company => company.sapCode);
      }
      return data;
    })();

    return companies.map(company => ({
      value: company.id,
      label: createLabel(company, options?.labelType),
    })).sort((a, b) => a.label.localeCompare(b.label));

  }, [options?.labelType, options?.siteType]);

  const companiesQuery = useQuery({
    queryKey: ['fetch-company-options', options?.siteType],
    queryFn: fetchCompanies,
    initialData: [],
    refetchOnWindowFocus: false,
  });

  const findCompany = React.useCallback((companyId: number | string) => {
    return companiesQuery?.data
      ? companiesQuery.data.find(company => company.id === +companyId)
      : undefined;
  }, [companiesQuery?.data]);

  return {
    singleSelectOptions: companiesQuery.data
      ? makeOptions(companiesQuery.data).map(optionsMapper)
      : [],
    multiSelectOptions: companiesQuery.data
      ? makeOptions(companiesQuery.data).map(multiSelectorOptionsMapper)
      : [],
    loading: companiesQuery.isLoading,
    findCompany,
  };
};